import { useEffect, useRef } from 'react';

export default function GangaIntro() {
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.2 }
    );
    if (textRef.current) observer.observe(textRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="hero"
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background image — Himalayan river at sunrise, Rishikesh-style */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/cd1e21057b3fe2e0eaf6b30ec5710aa4.jpg"
          alt="Ganga River in the Himalayas at sunrise"
          className="w-full h-full object-cover object-top"
        />
        {/* Primary dark gradient — heavier at top and bottom for text contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/28 to-black/62" />
        {/* Warm cinematic tone at bottom */}
        <div className="absolute inset-0 bg-gradient-to-t from-amber-950/25 via-transparent to-transparent" />
        {/* Subtle vignette for depth */}
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at center, transparent 38%, rgba(0,0,0,0.28) 100%)' }}
        />
      </div>

      {/* Content */}
      <div
        ref={textRef}
        className="relative z-10 text-center px-6 max-w-4xl mx-auto opacity-0 translate-y-8 transition-all duration-1000 ease-out"
      >
        <p className="text-amber-200/90 text-sm md:text-base uppercase tracking-[0.3em] mb-6 font-medium drop-shadow-sm">
          Environmental Analytics &amp; Documentary
        </p>

        <h1
          className="mb-6 leading-tight"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          <span className="block text-3xl md:text-5xl lg:text-6xl font-semibold text-white/90 drop-shadow-md">
            The Ganga&nbsp;
          </span>
          <span className="block text-5xl md:text-7xl lg:text-8xl font-bold text-amber-200 drop-shadow-lg mt-1">
            More Than a River
          </span>
        </h1>

        <div className="w-16 h-0.5 bg-amber-300/70 mx-auto mb-8" />

        <p className="text-white/85 text-lg md:text-xl leading-relaxed max-w-xl mx-auto mb-10 drop-shadow-sm tracking-wide">
          A sacred river. A growing crisis. A story that must be told.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
            href="#documentary"
            onClick={(e) => { e.preventDefault(); document.querySelector('#documentary')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="px-8 py-3.5 bg-amber-500 hover:bg-amber-400 text-white font-semibold rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-sm tracking-wide shadow-lg"
          >
            Watch Documentary
          </a>
          <a
            href="#about"
            onClick={(e) => { e.preventDefault(); document.querySelector('#about')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="px-8 py-3.5 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-full border border-white/65 hover:border-white/85 transition-all duration-300 cursor-pointer whitespace-nowrap text-sm tracking-wide backdrop-blur-sm shadow-md"
          >
            Explore Project
          </a>
        </div>
      </div>

      {/* Scroll indicator — improved visibility with bounce animation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
        <span className="text-white/85 text-xs tracking-widest uppercase font-medium drop-shadow-sm">Scroll</span>
        <div className="flex flex-col items-center gap-1 animate-bounce">
          <div className="w-px h-6 bg-gradient-to-b from-white/75 to-transparent" />
          <div className="w-1.5 h-1.5 rounded-full bg-white/65" />
        </div>
      </div>
    </section>
  );
}
